import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { FloppyDisk } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fields = [
  { key: "temp_min", label: "Min Temperature (\u00b0F)" },
  { key: "temp_max", label: "Max Temperature (\u00b0F)" },
  { key: "humidity_min", label: "Min Humidity (%)" },
  { key: "humidity_max", label: "Max Humidity (%)" },
  { key: "battery_min", label: "Min Battery Level (%)" },
];

export default function ThresholdSettings({ open, onClose }) {
  const [thresholds, setThresholds] = useState({
    temp_min: 32,
    temp_max: 40,
    humidity_min: 20,
    humidity_max: 80,
    battery_min: 20,
    light_alert_enabled: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchThresholds = async () => {
      try {
        const res = await axios.get(`${API}/thresholds`);
        setThresholds(res.data);
      } catch (e) {
        /* use defaults */
      }
    };
    fetchThresholds();
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/thresholds`, {
        temp_min: thresholds.temp_min,
        temp_max: thresholds.temp_max,
        humidity_min: thresholds.humidity_min,
        humidity_max: thresholds.humidity_max,
        battery_min: thresholds.battery_min,
        light_alert_enabled: thresholds.light_alert_enabled,
      });
      toast.success("Thresholds updated. Alerts regenerated.");
      onClose();
    } catch (e) {
      toast.error("Failed to save thresholds");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-zinc-900 border-zinc-800 sm:max-w-md"
        data-testid="threshold-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50">Alert Thresholds</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Set acceptable ranges for your cold chain parameters. Alerts are
            auto-generated when values exceed these thresholds.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {fields.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-4">
              <Label className="text-xs text-zinc-400 w-44 shrink-0">
                {label}
              </Label>
              <Input
                type="number"
                step="0.1"
                value={thresholds[key] ?? 0}
                onChange={(e) =>
                  setThresholds((prev) => ({
                    ...prev,
                    [key]: parseFloat(e.target.value) || 0,
                  }))
                }
                className="bg-zinc-950 border-zinc-800 font-mono text-sm focus:ring-1 focus:ring-teal-400"
                data-testid={`threshold-${key}`}
              />
            </div>
          ))}
          {/* Light Alert Toggle */}
          <div className="flex items-center gap-4 pt-2 border-t border-zinc-800">
            <Label className="text-xs text-zinc-400 w-44 shrink-0">
              Light Exposure Alerts
            </Label>
            <div className="flex items-center gap-2">
              <Switch
                checked={thresholds.light_alert_enabled ?? true}
                onCheckedChange={(checked) =>
                  setThresholds((prev) => ({
                    ...prev,
                    light_alert_enabled: checked,
                  }))
                }
                data-testid="threshold-light-alert"
              />
              <span className="text-[10px] font-mono text-zinc-500">
                {thresholds.light_alert_enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 font-semibold"
            data-testid="save-thresholds-btn"
          >
            <FloppyDisk size={16} className="mr-2" />
            {saving ? "Saving..." : "Save Thresholds"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
